import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultSettings, type AdminSettings } from '@/mocks/adminData';

function SettingsPage() {
  const [settings, setSettings] = useLocalStorage<AdminSettings>('admin_settings', defaultSettings);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [savedFlash, setSavedFlash] = useState(false);

  const update = (partial: Partial<AdminSettings>) => setSettings((prev) => ({ ...prev, ...partial }));

  const handleSave = () => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2000); };

  const handlePasswordChange = () => {
    if (!passwordForm.current || !passwordForm.newPass || !passwordForm.confirm) {
      alert('Preencha todos os campos de senha.');
      return;
    }
    if (passwordForm.newPass !== passwordForm.confirm) {
      alert('As senhas não conferem.');
      return;
    }
    setPasswordForm({ current: '', newPass: '', confirm: '' });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm">
          <i className="ri-check-line mr-1.5" />Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Configurações</h1>
          <p className="text-sm text-foreground-500 mt-1">Gerencie as configurações gerais do painel.</p>
        </div>
        <button type="button" onClick={handleSave} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap">
          <i className="ri-save-line" />Salvar alterações
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Admin Info */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Dados do administrador</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nome do administrador</label>
              <input type="text" value={settings.adminName} onChange={(e) => update({ adminName: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">E-mail de acesso</label>
              <input type="email" value={settings.adminEmail} onChange={(e) => update({ adminEmail: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Password */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Alterar senha</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Senha atual</label>
              <input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nova senha</label>
              <input type="password" value={passwordForm.newPass} onChange={(e) => setPasswordForm({ ...passwordForm, newPass: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Confirmar nova senha</label>
              <input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
            <button type="button" onClick={handlePasswordChange} className="px-4 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap">
              Alterar senha
            </button>
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Aparência</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Cor principal</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.primaryColor} onChange={(e) => update({ primaryColor: e.target.value })} className="w-10 h-10 rounded-lg border border-background-300 cursor-pointer p-1" />
                  <input type="text" value={settings.primaryColor} onChange={(e) => update({ primaryColor: e.target.value })} className="flex-1 px-3 py-2 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent font-mono" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Cor secundária</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.secondaryColor} onChange={(e) => update({ secondaryColor: e.target.value })} className="w-10 h-10 rounded-lg border border-background-300 cursor-pointer p-1" />
                  <input type="text" value={settings.secondaryColor} onChange={(e) => update({ secondaryColor: e.target.value })} className="flex-1 px-3 py-2 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent font-mono" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">Exibição no catálogo</h2>
          <div className="space-y-4">
            {[
              { key: 'showPrices' as const, label: 'Exibir preços no catálogo' },
              { key: 'showStock' as const, label: 'Exibir estoque' },
              { key: 'showFeatured' as const, label: 'Exibir produtos em destaque' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-foreground-700">{label}</span>
                <button
                  type="button"
                  onClick={() => update({ [key]: !settings[key] })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${settings[key] ? 'bg-primary-600' : 'bg-background-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings[key] ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* WhatsApp */}
        <div className="bg-white rounded-xl border border-background-200 p-5 lg:col-span-2">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4">WhatsApp</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Número do WhatsApp</label>
              <input type="text" value={settings.whatsappNumber} onChange={(e) => update({ whatsappNumber: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="55999260502" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Mensagem de finalização do carrinho</label>
              <input type="text" value={settings.cartFinalMessage} onChange={(e) => update({ cartFinalMessage: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;